import { Button, Modal } from 'antd';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import CourseHeading from '../../BookLandingPageComp/landingPage/heading/CourseHeading';
import { authModalControl } from '../../../atoms/authModalControl';
import { user } from '../../../atoms/user';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import { updateFollow } from '../../../services/School';
import { User } from '../../../utils/interfaces/User';

interface IAuthorDetails {
  authorImage?: string;
  authorName?: string;
  authorDescription?: string[];
}

interface BookDescriptionProps {
  data: IAuthorDetails;
  authorsData:
    | {
        follower_count: string;
        user_name: string;
        authorId: string;
        isFollowed: boolean;
      }
    | undefined;
  token: string;
  courseData?: {
    course_id: string;
    course_name: string;
  };
}

const CourseAuthorDetailsSection: React.FC<BookDescriptionProps> = ({
  data,
  authorsData,
  token,
  courseData,
}) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [, setVisible] = useRecoilState(authModalControl);
  const router = useRouter();

  const userData: User | undefined = useRecoilValue(user);

  const handleFollow = async ({
    author_id,
    is_follower,
  }: {
    author_id: string;
    is_follower: boolean;
  }) => {
    try {
      await updateFollow({
        author_id,
        is_follower,
      });
    } catch (error) {
      console.error('Error :', error);
    }
  };

  const handleFollowClick = ({
    state,
    author_id,
    author_name,
  }: {
    state: string;
    author_id: string;
    author_name: string;
  }) => {
    const eventParams: EventDetails = {
      event_name: state,
      source: 'global',
      source_type: 'author',
      source_id: author_id as string,
      source_name: author_name,
      sub_source: 'course',
      sub_source_id: courseData?.course_id,
      sub_source_name: courseData?.course_name,
      unit: 'boolean',
      value: 1,
      meta: {
        click_source: 'course_details_screen',
      },
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const toggleFollow = () => {
    const newState = !isFollowing;
    handleFollowClick({
      state: newState ? 'follow' : 'unfollow',
      author_id: authorsData?.authorId as string,
      author_name: data?.authorName as string,
    });

    setIsFollowing(newState);

    handleFollow({
      author_id: authorsData?.authorId as string,
      is_follower: newState,
    });
  };

  useEffect(() => {
    if (authorsData?.isFollowed) {
      setIsFollowing(true);
    }
  }, [authorsData?.isFollowed]);

  return (
    <div>
      <CourseHeading heading='Meet The Instructor' />
      <div className='flex flex-col  text-white border-[1px] border-[#282829] rounded-xl  shadow-lg'>
        {/* Profile Section */}
        <div
          style={{
            background: 'linear-gradient(180deg, #1C1C1C 0%, #000000 100%)',
          }}
          className='flex items-center  rounded-t-xl px-6 py-5 justify-between'
        >
          <div className='flex items-center gap-3.5'>
            <Image
              src={data.authorImage as string}
              alt={data?.authorName || 'Instructor'}
              width={48}
              height={48}
              className='rounded-full w-12 h-12 min-w-12 min-h-12 object-cover'
            />
            <div>
              <p className='text-base font-bold'>{data?.authorName}</p>
              <Link href={`/author/${authorsData?.user_name}`} target='_blank'>
                <span className='text-sm text-primary cursor-pointer hover:underline'>
                  View profile
                </span>
              </Link>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            <Button
              // shape='round'
              className='bg-[#282829] text-white border-none px-5 py-2'
              onClick={() => {
                if (token) {
                  isFollowing ? setIsModalVisible(true) : toggleFollow();
                } else {
                  setVisible((prevState: any) => ({
                    ...prevState,
                    login: true,
                    signUp: false,
                    noRedirect: true,
                  }));
                }
              }}
            >
              <motion.span
                key={isFollowing ? 'unfollow' : 'follow'}
                initial={{ y: isFollowing ? -10 : 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: isFollowing ? 10 : -10, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className='text-white'
              >
                {isFollowing ? 'Following' : 'Follow'}
              </motion.span>
            </Button>
          </motion.div>
        </div>

        {/* Instructor Details */}
        <div className='px-6 pb-3 '>
          <p className='text-white text-sm'>ABOUT THE INSTRUCTOR</p>
          <ul className='mt-4 mb-4 ml-2 text-sm space-y-1 text-white pl-1'>
            {data?.authorDescription?.map((desc, index) => (
              <li
                key={`${desc}-${index}`}
                className='flex items-start gap-2 text-white'
              >
                <span className='text-lg md:text-2xl h-full flex items-start -translate-y-1.5 '>
                  •
                </span>
                <span className='font-[300] text-xs md:text-sm '>{desc}</span>
              </li>
            ))}
          </ul>
        </div>
        <Modal
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width='400px'
          className='bg-[#212121] rounded-lg'
          closable={false}
          centered={true}
          destroyOnHidden
        >
          <div className='flex flex-col w-full'>
            <div className='py-5'>
              Sure want to Un-Follow {authorsData?.user_name} ?
            </div>
            <div className='flex gap-x-1 w-full  justify-end'>
              <span>
                <Button onClick={() => setIsModalVisible(false)} type='text'>
                  No
                </Button>
              </span>
              <span>
                <Button
                  onClick={() => {
                    toggleFollow();
                    setIsModalVisible(false);
                  }}
                  type='text'
                  className='text-blue-500 font-medium'
                >
                  Yes
                </Button>
              </span>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default CourseAuthorDetailsSection;
